import {
    type CornCropMonitoring,
    CROP_MONITORING_KEYS,
    type CropMonitoring,
    type CropType,
} from '../types/crop-monitoring';
import type { HistoricalWeather } from '../types/weather/forecast';

// Utility functions
export const getCropMonitoringKey = (crop: CropType): keyof CropMonitoring => {
    return CROP_MONITORING_KEYS[crop];
};

export const getCropMonitoringData = (cropMonitoring: CropMonitoring | undefined) => {
    if (!cropMonitoring) return;
    const key = getCropMonitoringKey(cropMonitoring.crop);
    return cropMonitoring[key] as CornCropMonitoring;
};

/**
 * True while an active corn CM may still be processed asynchronously on the server
 * (e.g. weather/GDU job after create or update). Used to drive short-lived SWR polling.
 */
export const cropMonitoringPendingAsyncState = (cropMonitoring: CropMonitoring | undefined): boolean => {
    if (!cropMonitoring?.active) return false;
    if (cropMonitoring.crop !== 'corn') return false;
    const corn = cropMonitoring.cornCropMonitoring;
    if (!corn) return true;
    if (corn.phenology === 'harvested') return false;
    return !corn.accumulatedGduDate;
};

/**
 * Accumulated GDUs to display for a corn CM.
 *
 * The backend keeps accumulating `accumulatedGdu` daily until harvest, but GDUs stop
 * counting at physiological maturity (MF). Once an MF stage exists, freeze the number at
 * the accumulated value on the MF date, read from the same daily series the GDU graphic
 * plots (`gduAccum`) so card and graphic always agree.
 *
 * Falls back to the raw `accumulatedGdu` before MF, or when the MF day is missing from
 * the weather series (not loaded yet, or a gap in the data).
 */
export const getDisplayAccumulatedGdu = (
    cropMonitoring: CropMonitoring | undefined,
    historicalWeather: HistoricalWeather | undefined
): number | undefined => {
    const cropData = getCropMonitoringData(cropMonitoring);
    if (!cropData) return undefined;

    const maturityStage = cropData.stages?.find((stage) => stage.phenology === 'maturity');
    if (!maturityStage) return cropData.accumulatedGdu;

    // Compare the calendar day as a string: stage dates are date-only ("2026-02-15") while
    // weather dates carry a time part, so parsing both to `Date` would shift them onto
    // different local days and silently miss the match.
    const maturityDay = maturityStage.date.split('T')[0];
    const maturityWeather = historicalWeather?.results?.find((hw) => hw.date.split('T')[0] === maturityDay);
    if (maturityWeather?.gduAccum === undefined) return cropData.accumulatedGdu;

    return Math.round(maturityWeather.gduAccum);
};

export const isFallowPeriod = (plantingDate: string): boolean => {
    return new Date(plantingDate) > new Date();
};
